import { getAsset } from "./store";
import { parseKmz, type LoadedOverlay } from "../overlay/kmzOverlay";

/**
 * Rebuild the team's KMZ overlays from the copies cached under this flight's
 * keys, for when `/api/overlays` is unreachable (the service worker never
 * intercepts `/api/*` by design).
 *
 * The cached manifest is the source of truth, never the `:overlay:*` blobs:
 * an overlay the team has since removed can leave a stale blob behind that is
 * absent from the manifest, and it must not be surfaced. Entries with no blob
 * are skipped. A missing manifest yields `[]` — the flight was cached before
 * Plan 13, or the team had no overlays at download time. Never throws.
 */
export async function loadCachedOverlays(flightId: string): Promise<LoadedOverlay[]> {
  const manifestRec = await getAsset(`${flightId}:overlays-manifest`);
  if (manifestRec === null) return [];
  let list: Array<{ id: string; name: string }>;
  try {
    const parsed = JSON.parse(await manifestRec.blob.text()) as unknown;
    // Valid JSON of the wrong shape must degrade like corrupt bytes, not throw.
    if (!Array.isArray(parsed)) return [];
    list = parsed as Array<{ id: string; name: string }>;
  } catch {
    return [];
  }
  const loaded: LoadedOverlay[] = [];
  for (const { id, name } of list) {
    const rec = await getAsset(`${flightId}:overlay:${id}`);
    if (rec === null) continue;
    try {
      const fc = await parseKmz(await rec.blob.arrayBuffer());
      if (fc === null) continue;
      loaded.push({ id, name, fc });
    } catch {
      // Unreadable cached blob — skip this overlay, keep the rest.
    }
  }
  return loaded;
}
